import { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Search, X } from "lucide-react";

/**
 * Typeahead item picker — searches by item_number, description, category.
 * value: current selected item_number (string)
 * onChange: (item_number, item_obj) => void
 */
export function ItemCombobox({ value, onChange, testid, placeholder = "Código, descripción o No. de parte…", disabled }) {
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(0);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    if (q === value) return; // don't search when input matches selected
    const t = setTimeout(() => {
      setBusy(true);
      api.get("/items/search", { params: { q, limit: 15 } })
        .then((r) => { setResults(r.data); setHoveredIdx(0); setOpen(true); })
        .finally(() => setBusy(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q, value]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (it) => {
    setQ(it.item_number);
    setOpen(false);
    onChange(it.item_number, it);
  };

  const clear = () => {
    setQ("");
    setResults([]);
    onChange("", null);
    inputRef.current?.focus();
  };

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHoveredIdx((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHoveredIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(results[hoveredIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef} data-testid={testid}>
      <div className="relative">
        <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={q}
          disabled={disabled}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q && setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          className="rounded-none h-8 text-xs pl-6 pr-6 font-mono"
          data-testid={`${testid}-input`}
        />
        {q && !disabled && (
          <button type="button" onClick={clear}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-destructive"
            data-testid={`${testid}-clear`}>
            <X size={12} />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute top-full left-0 right-0 z-50 mt-0.5 border bg-popover shadow-lg max-h-72 overflow-y-auto" data-testid={`${testid}-results`}>
          {busy && <div className="p-2 text-[11px] text-muted-foreground text-center">Buscando…</div>}
          {!busy && results.length === 0 && q.trim() && (
            <div className="p-3 text-[11px] text-muted-foreground text-center">Sin resultados para "{q}"</div>
          )}
          {results.map((it, i) => (
            <button key={it.id} type="button" onClick={() => pick(it)}
              onMouseEnter={() => setHoveredIdx(i)}
              className={`w-full text-left px-3 py-2 border-b last:border-b-0 hover:bg-muted transition-colors ${
                i === hoveredIdx ? "bg-muted" : ""
              }`}
              data-testid={`${testid}-result-${it.item_number}`}>
              <div className="flex justify-between gap-2 items-baseline">
                <span className="text-[11px] font-black tabular-nums text-primary">{it.item_number}
                  {it.part_number && <span className="ml-2 font-mono font-bold text-[10px] text-amber-700 dark:text-amber-400" title="No. de parte">NP {it.part_number}</span>}
                </span>
                <span className="text-[10px] text-muted-foreground truncate">{it.category || ""}</span>
              </div>
              <div className="text-xs mt-0.5 truncate">{it.description}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
